import type { BinanceShortLiveEvent } from './BinanceAlertShortLivePanel';
import { kstToMs } from './chartPoints';
import { u } from '../shared/hardcodedKoEn';

export type BinanceAlertRow = {
  key: string;
  alert: BinanceShortLiveEvent;
  entry: BinanceShortLiveEvent | null;
  exit: BinanceShortLiveEvent | null;
  /** abort→롱 전환. 숏이 abort 로 끊긴 바로 그 틱에 새로 열린 롱 -- open_abort_long()
   *  이 그 숏의 settle 과 같은 tick 에서 불려 time_kst 가 정확히 같다. long_entry
   *  는 signal/bh_signal 뒤가 아니라 이 숏의 결과로 생기므로 독립 행이 아니라
   *  이 행에 딸린 다리다(2026-08-07: 이 연결이 없어서 abort→long 거래 전체가
   *  대시보드에서 통째로 보이지 않았다). */
  longLeg: { entry: BinanceShortLiveEvent; exit: BinanceShortLiveEvent | null } | null;
  /** 재숏 (2026-08-22). abort->롱 으로 탈출한 뒤 **같은 알림에서 다시 연** 숏이다.
   *  독립 행으로 세웠다가 되돌렸다: 그러면 한 거래의 사슬이 두 행으로 쪼개져
   *  LONG 다리는 알림 행에, 재숏은 다른 행에 남는다(유저 지적 -- "13:59 EXIT 한
   *  무브먼트에 Detail 에 재숏이라고만 나와있어"). 사슬은 한 행에서 읽혀야 한다:
   *  ALERT -> SHORT -> EXIT -> LONG -> LONG-EXIT -> RESHORT -> EXIT.
   *  longLeg 과 같은 모양(진입+결말)이고, 결말은 자기 settle 이다. */
  reshortLeg: { entry: BinanceShortLiveEvent; exit: BinanceShortLiveEvent | null } | null;
  /** 추가 진입(2026-08-11). **같은 거래의 두 번째 다리**라 독립 행이 아니다 --
   *  본진입 아래에 SHORT 한 줄이 더 붙는다. 청산은 본진입과 한 번에 나가므로
   *  자기 exit 이 없다(정산 %는 합산 노셔널 기준이라 그 행 하나로 맞는다). */
  addon: BinanceShortLiveEvent | null;
};

// Must cover every intake-stage `skip_*` the trader emits, because
// groupBinanceAlertRows() drops any non-`signal` event that is in neither this
// set nor ENTRY_OUTCOME_ACTIONS -- silently, with no row and no warning.
// Source of truth: TYPE_LABEL in mercury-binance
// alert-live/scripts/alert_binance_short_live_monitor.py.
// 2026-07-29: skip_early20_weak was missing since the rule shipped, so
// "10% 뒤 2시간 안의 20% 재알림" skips never appeared on the dashboard at all
// (KRW-EUL 12:17 was in the API payload and rendered nothing).
const INTAKE_SKIP_ACTIONS = new Set([
  'skip_unmapped',
  'skip_unresolved',
  'skip_segment_high',
  // 업숏 시즌 게이트 (2026-10-08). 여기 없으면 signal 없이 오는 SKIP 이
  // groupBinanceAlertRows 에서 조용히 버려진다 (07-29 skip_early20_weak).
  'skip_segment_low',
  'skip_off_hours',
  'skip_runup_low',
  'skip_btc60_high',
  'skip_concurrent_low',
  'skip_early20_weak',
  'skip_halted',
  'skip_dedup',
  'skip_non_upbit_source',
]);

const ENTRY_OUTCOME_ACTIONS = new Set([
  'short',
  // linear 스케일(2026-09-07). 트리거 알림(다른 signal_id) 행의 **진입**으로
  // 붙인다 — 본진입 행에 addon 으로 묶지 않는다(차트·진입표시가 그 행에 필요).
  'addon_short',
  'long_entry',
  'entry_fail',
  'long_entry_fail',
  'skip_late',
  'skip_slot_full',
  'skip_min_notional',
  // linear 레인 마진 캡 (2026-09-07). flat×rung 이 한도를 넘으면 0.
  'skip_linear_margin',
  // 진입 직전 게이트 재확인 탈락(2026-08-13). **인테이크 스킵이 아니다** --
  // 알림 때는 통과해 signal 행이 이미 있고, 진입 시점에 문턱이 올라가 걸린 것이라
  // 그 행의 **결과**로 붙어야 한다. 인테이크 집합에 넣으면 별도 행이 되고 원래
  // signal 행은 결과를 못 찾아 영원히 '대기중'으로 남는다(08-13 KRW-HOME).
  'skip_gate_recheck',
  // 청산 레인 증거금 예약(2026-08-15 KRW-ARX). skip_gate_recheck 와 같은 자리
  // -- 알림은 통과해 signal 이 있고, 진입 직전 지갑이 막는다. 여기 없으면 그
  // SKIP 은 조용히 버려지고 signal 행의 PENDING 만 시간 지나 사라진다.
  'skip_liq_reserve',
  // 남은 여유로 최소 주문도 안 되는 업숏 (2026-10-22). signal 뒤 결말.
  'skip_free_margin',
  // 청캐 우선(2026-09-06): 청캐 열림·감시 중이라 업숏이 양보. signal 뒤 결말.
  'skip_liq_priority',
  // 청캐 누적 75+ 양보 (2026-10-21). signal 뒤 결말.
  'skip_liq_near',
  // OI 밀림 (2026-10-22). signal 뒤 결말.
  'skip_oi_push',
  // mid 빠른 청캐사다리 (2026-10-23). signal 뒤 결말.
  'skip_liq_climb_mid',
  // 저녁 15분 대기 중 다음 구간 상승 (2026-10-23 KRW-ARK). 여기 없으면
  // 이벤트가 버려지고 signal 행이 영원히 WATCH.
  'skip_climb15',
  // 원웨이 업숏 심볼당 1장부 (2026-09-06 FLOCK). 두 번째 알림은 지나감.
  'skip_already_open',
  // 레인 충돌(2026-08-14). 같은 심볼을 다른 레인이 이미 들고 있어 진입이 접힌다.
  'skip_lane_busy',
  // 되돌림 감시창 만료(2026-08-25 KRW-ONG). signal 뒤에 오는 **그 알림의 결말**이다.
  // 여기 없으면 이벤트가 조용히 버려지고, 앞선 signal 이 나중 signal 의 short 를
  // 훔쳐 12:53 행이 ENTRY 로·16:37 행이 PENDING 으로 보인다.
  'skip_no_pullback',
  // 업숏 진입직후 재확인창 역행 컷 (2026-09-06). signal 뒤 결말.
  'skip_confirm_reject',
  // 업숏 주문 스위치 OFF (2026-08-26). signal 뒤 진입 직전 결말 -- cl_skip_not_live
  // / bh sleeve_paused 와 같은 자리.
  'skip_not_live',
  // 빗썸 new_high 슬리브 (prereg 2026-08-01). 신호는 빗썸 알림만 쓰고 주문은
  // 다른 레인과 똑같이 바이낸스 퍼프 숏이다. bh_signal 뒤에 오는 진입단계 결과들:
  'bh_short',
  'bh_entry_fail',
  'bh_skip_late',
  'bh_skip_slot_full',
  'bh_skip_capacity',
  'bh_skip_min_notional',
  'bh_skip_margin',
  // 빗썸 되돌림 감시 만료 -- 업비트 skip_no_pullback 과 같은 자리. 인테이크
  // (bh_skip_* 기본값)로 두면 단독 행이 되고 bh_signal 은 결말 없이 PENDING.
  'bh_skip_no_pullback',
  // 되돌림은 확인됐는데 주문 스위치가 꺼져 있어 못 들어간 건 (2026-09-04).
  // bh_signal 뒤 진입 직전 결말 -- skip_not_live / cl_skip_not_live 와 같은 자리.
  // 여기 없으면 그 알림 행이 영원히 WATCH 로 남고, 스킵이 되돌림 확인 시각에
  // 별도 ALERT 행으로 뜬다 (KRW-TAIKO 19:53 알림 → 20:20 sleeve_paused).
  'bh_skip_sleeve_paused',
  // 업숏 ON + 쌍숏이 업 쪽으로 잡은 빗 알림 (2026-09-06 IOST 10:51).
  // 여기 없으면 되돌림·sleeve_paused 까지 WATCH 로 남는다.
  'bh_skip_upbit_handled',
  // 업숏이 쌍숏에 양보 (upbit_skip_if_dual). 라이브 action=skip_ds_yield.
  // 여기 없으면 signal 행이 영원히 WATCH (ds_short 접기 실패 시 특히).
  'skip_ds_yield',
  // 편승 즉시진입 실패(2026-08-27). pr_candidate 뒤 결말 -- slot_full·
  // fast_run_skip 등. 여기 없으면 pr_skip 이 조용히 버려지고 감시행이 영원히
  // WATCH 로 남는다(cl_skip / liq_skip 과 같은 함정).
  'pr_skip',
  // 쌍숏(2026-09-02). ds_candidate(감시 30분) 뒤 결말 -- no_pullback·slot_full·
  // capacity·margin·live_off. 여기 없으면 감시행이 영원히 WATCH.
  'ds_skip', 'ds_short', 'ds_entry_fail',
]);

/** 쌍숏 ds_skip 중 **감시 등록 전** 사유 — 머리(ds_candidate)가 없어 단독 행이
 *  된다. 나머지(no_pullback·slot_full·lane_busy·capacity·margin·live_off)는
 *  ds_candidate 뒤에 와 그 행에 접힌다. */
const DS_PRE_WATCH_SKIP_REASONS = new Set([
  'not_dormant', 'no_perp', 'no_range_data', 'excluded', 'not_dual',
]);

/** 슬리브 알림(= 행의 머리)이 되는 액션.
 *  liq_entry 가 여기 있는 이유: 청산 캐스케이드 레인은 **알림이 없다**. 트리거가
 *  곧 진입이라 진입 이벤트 자체가 행의 머리다. 여기 안 넣으면 groupBinanceAlertRows
 *  가 조용히 버려서(ALERT_ACTIONS 도 ENTRY_OUTCOME 도 아니면 return) 이 레인의
 *  거래가 통째로 화면에서 사라진다 -- 07-29 skip_early20_weak 와 같은 부류의 사고다.
 *
 *  cl_entry 도 같은 이유로 여기 있다: 후보-롱은 빗썸 알림을 **후보로만** 쓰고
 *  트리거(레벨 AND 속도)가 곧 진입이라, 알림 행에 붙지 않고 자기 행의 머리가
 *  된다(lane 'cl' 로 갈려 bh_signal 과 sameTrade 가 아니다). */
const ALERT_ACTIONS = new Set([
  'signal', 'bh_signal', 'liq_entry', 'liqv2_entry',
  // 청캐 v2 조각 추가 — 각자 SHORT 행 (묶지 않음, 2026-09-13).
  'liqv2_add',
  // 청캐 v3(얕은산 단건). 알림 없는 진입 머리 (2026-10-07).
  'liqv3_entry',
  // 청산 레인에서 **진입이 안 된 트리거**의 머리(2026-08-22). 이 레인은 알림이
  // 없어서 liq_entry 가 머리인데, 규칙이 보고 접은 건은 liq_entry 가 없다 --
  // 그러면 붙을 머리가 없어 조용히 사라진다. 되돌림 진입(liq_pullback_*)이
  // 켜지면서 **트리거의 절반 이상**이 no_pullback 으로 여기 빠지므로, 이게
  // 없으면 판정 모집단이 화면에서 반토막 난다. 증거금(margin)으로 접힌 건도
  // 같은 이유로 여태 안 보였다(07-29 skip_early20_weak 와 같은 부류).
  'liq_skip',
  // 청산 레인 **감시 시작**(2026-08-22). 되돌림 진입이 켜지면서 트리거와 진입
  // 사이에 최대 15분이 생겼는데, 그 구간이 화면에 없어서 되돌림을 기다린 진입이
  // **트리거 즉시 진입처럼** 보였다(유저: "바로 진입하고 있는거야? WATCH 하는
  // 시간이 있지 않았어?" -- 실제로는 5~8분 기다렸다). 결말(liq_entry/liq_skip)은
  // 같은 signal_id 를 달고 와 아래에서 이 행에 접힌다.
  'liq_watch',
  // 후보-롱은 머리가 둘일 수 있다:
  //  · cl_candidate — 알림을 후보로 잡은 시점(트리거 전). 발동률이 30% 라
  //    이게 없으면 **나머지 70% 가 화면에서 통째로 사라진다** -- "규칙이 안
  //    잡았다" 와 "감시했는데 조건이 안 찼다" 가 구분되지 않는다.
  //  · cl_entry — 트리거가 곧 진입이라 자기 행의 머리이기도 하다(후보 행과
  //    같은 lane 'cl' 이지만 sid 가 달라 sameTrade 로 묶이지 않는다).
  'cl_candidate', 'cl_entry',
  // 편승(2026-08-27). **pr_candidate 만** 머리 — pr_entry 는 여기 넣지 않는다.
  // 넣으면 pr_entry 가 자기 ALERT 행(진입 시각)으로 뜨고, 중간에 다른 ONG
  // pr_candidate 가 끼면 옛 감시(07:07)가 pr_entry(10:42) 와 못 묶인다
  // (2026-08-27 KRW-ONG 실측). pr_trigger_paper 는 알림 없는 페이퍼 트리거 행.
  'pr_candidate', 'pr_trigger_paper',
  // 겹롱은 알림이 없다. 진입·주문 실패가 곧 행의 머리다. 여기 없으면
  // groupBinanceAlertRows 가 gl_* 를 버린다.
  'gl_watch', 'gl_entry', 'gl_entry_fail',
  // 쌍숏(2026-09-02). ds_candidate 만 머리 — 업+빗 동시펌프를 후보로 잡은 시점
  // (되돌림 대기 시작). 발동률 ~65% 라 이게 없으면 나머지가 화면에서 사라진다.
  // 결말(ds_short/ds_skip)은 같은 signal_id 로 와 아래에서 이 행에 접힌다.
  'ds_candidate',
]);

const EXIT_FOR_ENTRY: Record<string, string> = {
  short: 'settle',
  // 재숏의 결말도 settle 이다(같은 레인·같은 정산 경로).
  reshort: 'settle',
  // linear 스케일: 본진입과 **같은 포지션** 청산. 매핑 빠지면 exit=null →
  // 차트가 live 꼬리로 계속 움직인다 (2026-09-07 IOST 15:35).
  addon_short: 'settle',
  long_entry: 'long_settle',
  bh_short: 'bh_settle',
  liq_entry: 'liq_settle',
  liqv2_entry: 'liqv2_settle',
  // 조각도 같은 슬리브 정산을 공유 (2026-09-13).
  liqv2_add: 'liqv2_settle',
  liqv3_entry: 'liqv3_settle',
  cl_entry: 'cl_settle',
  pr_entry: 'pr_settle',
  gl_entry: 'gl_settle',
  ds_short: 'ds_settle',
};

/** 어느 레인의 이벤트인가. 빗썸 슬리브는 액션 이름이 전부 bh_ 로 시작한다.
 *  후보-롱(cl_)은 **자기 레인**이다: 빗썸 알림을 공유하지만 풀(open_cl)도 방향도
 *  다르다. 'bh' 로 합치면 sameTrade 가 페이드-숏과 후보-롱을 한 행으로 뭉갠다.
 *  편승(pr_)도 자기 레인(open_pr). */
function lane(ev: BinanceShortLiveEvent): 'bh' | 'upbit' | 'liq' | 'cl' | 'pr' | 'ds' | 'gl' {
  // 청캐 v2/v3 도 청캐 레인이다. `liqv2_`/`liqv3_` 는 `liq_` 로 시작하지
  // 않아서 빠뜨리면 기본값(업숏)으로 떨어진다 — 2026-09-06 ARBUSDT 가 업비트
  // 행으로 나왔다.
  if (ev.action.startsWith('liq_')
      || ev.action.startsWith('liqv2_')
      || ev.action.startsWith('liqv3_')) return 'liq';
  if (ev.action.startsWith('cl_')) return 'cl';
  if (ev.action.startsWith('pr_')) return 'pr';
  if (ev.action.startsWith('ds_')) return 'ds';
  // 겹롱을 업숏으로 두면 같은 퍼프의 업숏 행과 한 줄로 뭉개진다.
  if (ev.action.startsWith('gl_')) return 'gl';
  return ev.action.startsWith('bh_') ? 'bh' : 'upbit';
}

/** 쌍숏 결말을 부모 알림(업 `signal` / 빗 `bh_signal`)에 붙일지.
 *  Ex 칸을 바꾸지 않는다 — 업숏 행에 ds_short 만 접는다 (2026-09-06 IOST).
 *  업숏 우선: attr_lane 없거나 upbit 이면 `signal` 만, bh 면 `bh_signal` 만. */
function dsChildOfParent(
  parent: BinanceShortLiveEvent,
  child: BinanceShortLiveEvent,
): boolean {
  if (!child.action.startsWith('ds_')) return false;
  if (parent.symbol !== child.symbol || parent.perp !== child.perp) return false;
  const attr = child.attr_lane;
  if (parent.action === 'signal') {
    return attr !== 'bh';
  }
  if (parent.action === 'bh_signal') {
    return attr === 'bh';
  }
  return false;
}

function hasDsParentAlert(
  sorted: BinanceShortLiveEvent[],
  dsEv: BinanceShortLiveEvent,
): boolean {
  const wantBh = dsEv.attr_lane === 'bh';
  return sorted.some((p) => {
    if (p.symbol !== dsEv.symbol || p.perp !== dsEv.perp) return false;
    if (wantBh) return p.action === 'bh_signal';
    return p.action === 'signal';
  });
}

function sameTrade(a: BinanceShortLiveEvent, b: BinanceShortLiveEvent): boolean {
  // 레인까지 봐야 한다. 두 레인은 같은 심볼을 동시에 들 수 있고(풀이 분리돼 있다),
  // symbol+perp 만 보면 업비트 signal 이 빗썸 슬리브의 bh_short 를 자기 진입으로
  // 집어가 두 거래가 한 행으로 뭉개진다.
  const base = a.symbol === b.symbol && a.perp === b.perp && lane(a) === lane(b);
  if (base) return true;
  // gl_entry 는 symbol 을 안 싣는다. 대기는 perp 를 symbol 로 넣어서
  // 둘이 안 붙고, 대기는 15초 뒤 IGNORÉ·진입은 다른 줄이 된다 (IOST 12:09).
  if (lane(a) === 'gl' && lane(b) === 'gl' && a.perp && a.perp === b.perp) {
    const asym = a.symbol || a.perp;
    const bsym = b.symbol || b.perp;
    if (asym === bsym) return true;
  }
  // 청캐 skip -> 편승 버스트롱(liq_kr_skip) 연결(2026-09-03 HEMI).
  // 레인은 liq 와 pr 로 다르지만 같은 signal_id 사슬 하나다.
  const aLiq = a.action.startsWith('liq_') || a.action.startsWith('liqv2_')
    || a.action.startsWith('liqv3_');
  const bLiq = b.action.startsWith('liq_') || b.action.startsWith('liqv2_')
    || b.action.startsWith('liqv3_');
  const aPrLiqKr = a.action.startsWith('pr_') && a.source === 'liq_kr_skip';
  const bPrLiqKr = b.action.startsWith('pr_') && b.source === 'liq_kr_skip';
  if (!((aLiq && bPrLiqKr) || (bLiq && aPrLiqKr))) return false;
  if (a.perp !== b.perp) return false;
  if (a.signal_id && b.signal_id) return a.signal_id === b.signal_id;
  return true;
}

/** 업숏·빗숏 알림→결말. signal_id 가 양쪽에 있으면 **그것으로만** 잇는다
 *  (2026-09-07 IOST). sameTrade 만 쓰면 나중 알림이 앞 알림의 short 를 훔치고,
 *  앞 알림은 stopAt(다음 signal)에 막혀 WATCH 로 남는다. */
function sameTradePreferSignalId(
  a: BinanceShortLiveEvent,
  b: BinanceShortLiveEvent,
): boolean {
  if (!sameTrade(a, b)) return false;
  if (a.signal_id && b.signal_id) return a.signal_id === b.signal_id;
  return true;
}

/** 빗숏 고점 미달 문장에서 화면이 붙일 부분만.
 *  시각·IGNORÉ·틱 수·감시 분은 행에 넣지 않는다 (2026-10-23 문버드). */
function bhPeakLowNote(detail: string | null | undefined): string {
  const s = (detail || '').trim();
  const m = s.match(/고점 미달 · 최대 [+-][\d.]+% < \+[\d.]+%/);
  return m ? m[0] : '';
}

/** 알림 행 Detail 에 붙일 스킵 문장. 고점 미달은 짧은 비교만. */
function bhSkipFoldNote(ev: BinanceShortLiveEvent): string {
  if (ev.action === 'bh_skip_peak_low') return bhPeakLowNote(ev.detail);
  const d = (ev.detail || '').trim();
  if (ev.action === 'bh_skip_peak_age' && (!d || d === 'peak_age')) {
    return u('고점 뒤 15분 초과');
  }
  return d;
}

function sameTradeOrDsChildPreferSignalId(
  a: BinanceShortLiveEvent,
  b: BinanceShortLiveEvent,
): boolean {
  return sameTradePreferSignalId(a, b) || dsChildOfParent(a, b);
}

function isIntakeSkip(ev: BinanceShortLiveEvent): boolean {
  if (INTAKE_SKIP_ACTIONS.has(ev.action)) return true;
  // 슬리브 스킵은 백엔드가 bh_skip_<reason> 으로 펴서 보낸다. 진입단계 사유는
  // 위 집합에 열거돼 있고, 나머지(runup_low/upbit_prior/dedup, 그리고 앞으로
  // 생길 새 사유)는 전부 인테이크 스킵으로 본다 -- 열거에서 빠진 이름이
  // 조용히 행째로 사라지는 것을 막는 기본값이다.
  if (ev.action.startsWith('bh_skip_') && !ENTRY_OUTCOME_ACTIONS.has(ev.action)) return true;
  // 후보-롱(2026-08-13): 스킵·진입실패는 **포지션이 안 열린 것**이라 붙을 진입
  // 행이 없다. 단독 행으로 세우지 않으면 groupBinanceAlertRows 의
  // `if (!ALERT_ACTIONS.has(...)) return` 에서 통째로 버려진다.
  // 특히 cl_skip_not_live 는 "규칙은 쳤는데 주문 스위치가 꺼져 있었다" 이고,
  // 지금 이 레인의 **포워드 판정 모집단이 정확히 그 행들**이다 -- 사라지면
  // "규칙이 안 잡았다" 와 구분이 안 된다.
  if (ev.action.startsWith('cl_skip_') || ev.action === 'cl_entry_fail') return true;
  // 쌍숏(2026-09-02): 감시 등록 **전에** 접힌 ds_skip 은 머리(ds_candidate)가
  // 없다 — 업+빗 동시가 안 맞거나, 동시여도 dormant/무기한/제외에서 걸린 것.
  // 이 건들은 단독 행으로 세워야 groupBinanceAlertRows 의
  // `if (!ALERT_ACTIONS.has(...)) return` 에서 조용히 사라지지 않는다
  // (2026-09-02 KRW-ZKP not_dormant: 화면에 아무것도 안 떴다).
  // 감시 뒤 결말(no_pullback/slot_full/margin/live_off)은 ds_candidate 에
  // 접히므로 여기서 뺀다.
  if (ev.action === 'ds_skip') {
    return DS_PRE_WATCH_SKIP_REASONS.has(ev.reason ?? '');
  }
  return false;
}

function entryOutcomeLabel(ev: BinanceShortLiveEvent): 'SHORT' | 'RESHORT' | 'LONG' | 'SKIP' | 'FAIL' | 'COLLECT' | 'WATCH' {
  if (ev.action === 'short' || ev.action === 'bh_short'
      || ev.action === 'liq_entry' || ev.action === 'ds_short'
      || ev.action === 'liqv2_entry' || ev.action === 'liqv2_add'
      || ev.action === 'liqv3_entry'
      // 추가 진입도 숏이다 -- 여기 없으면 기본값 SKIP 으로 떨어져 화면에
      // "SKIP" 이라고 뜬다(진입했는데 스킵으로 보이는 최악의 오표시).
      || ev.action === 'addon_short') return 'SHORT';
  // 재숏은 **자기 라벨을 갖는다** (2026-08-22 유저 지적). SHORT 로 찍으면 Status
  // 칸에서 보통 진입과 구분이 안 되고, "재숏이다" 라는 사실이 Detail 문장 안에만
  // 남는다 -- Detail 은 3줄에서 잘리는 칸이라 거기 묻히면 못 본다. 이건 abort->롱
  // 탈출 뒤에 **다시 연** 포지션이라, 원래 진입과 성격이 다르고 성적도 따로 본다.
  if (ev.action === 'reshort') return 'RESHORT';
  // 후보-롱은 방향이 **롱**이다. SHORT 로 찍히면 화면이 정반대를 말한다.
  if (ev.action === 'long_entry' || ev.action === 'cl_entry'
      || ev.action === 'pr_entry' || ev.action === 'gl_entry') return 'LONG';
  if (ev.action === 'entry_fail' || ev.action === 'long_entry_fail'
      || ev.action === 'bh_entry_fail' || ev.action === 'cl_entry_fail'
      || ev.action === 'pr_entry_fail' || ev.action === 'ds_entry_fail'
      || ev.action === 'gl_entry_fail') return 'FAIL';
  // 2026-07-25: Bithumb/Coinone alerts are captured but never traded (see
  // alert_binance_short_live.py's collection-only gate) -- distinct from the
  // strategy's own filter skips (segment/runup/etc.), so give it its own label
  // rather than lumping it in with SKIP.
  // 후보-롱 감시 등록은 수집도 스킵도 아니다 -- 아직 판단 중이다.
  if (ev.action === 'cl_candidate' || ev.action === 'liq_watch'
      || ev.action === 'pr_candidate' || ev.action === 'pr_trigger_paper'
      || ev.action === 'ds_candidate' || ev.action === 'gl_watch') return 'WATCH';
  if (ev.action === 'skip_non_upbit_source') return 'COLLECT';
  return 'SKIP';
}

export function isPositionEntry(action?: string): boolean {
  return action === 'short' || action === 'long_entry' || action === 'bh_short'
    || action === 'liq_entry' || action === 'cl_entry' || action === 'pr_entry'
    || action === 'ds_short'
    // 청캐 v2(사다리). 빠지면 실제로 주문이 나간 행이 "진입 아님" 으로 칠해진다
    // (2026-09-06 ARBUSDT).
    || action === 'liqv2_entry'
    || action === 'liqv2_add'
    || action === 'liqv3_entry'
    || action === 'gl_entry'
    // 재숏은 **주문이 실제로 나간** 진입이다. 빠지면 "진입(예정)" 스위치가
    // 그 행을 감춘다 -- 돈이 걸린 행을 감추는 것은 이 스위치의 정반대 목적이다.
    || action === 'reshort'
    // linear 스케일 증액도 실주문. 트리거 알림 행에 ENTRY 로 붙인다
    // (2026-09-07 IOST — 묶지 말고 각 행에 진입표시·차트).
    || action === 'addon_short';
}

/**
 * Detail 칸 한 줄에 들어가는 폭(한글 2u / 라틴 1u).
 *
 * 좁은 쪽 레이아웃(.dashboard-broker-row → 640px 표의 26% ≈ 166px)에 Detail 칸
 * 폰트 0.68rem 기준. dashboardMobile.css 의
 * `.binance-alert-col-detail .broker-3line-line { font-size: 0.68rem }` 와
 * 짝이므로 **한쪽만 바꾸면 안 된다** -- 폰트를 키우면 여기를 줄여야 한다.
 *
 * 넘쳐도 줄바꿈은 일어나지 않는다(nowrap 유지 = 행 높이 고정). 대신 잘린다.
 * 그래서 실측 최장 Detail 53u 가 3줄에 확실히 들어가는 값이어야 하고,
 * binanceAlertRows.test.ts 가 실제 문자열들로 그걸 검증한다.
 */
export const DETAIL_LINE_UNITS = 25;
const WIDE_CHAR = /[ᄀ-ᇿ　-ヿ㄰-㆏가-힯＀-｠]/;

export function textUnits(s: string): number {
  let n = 0;
  for (const ch of s) n += WIDE_CHAR.test(ch) ? 2 : 1;
  return n;
}

/** 비교 연산자. 양옆 토큰과 한 덩어리로 묶여 절대 줄을 넘지 않는다. */
const OPERATOR = /^(<|<=|>|>=|→|==)$/;

/**
 * 한 구절을 "끊으면 안 되는 덩어리"들로 나눈다.
 *
 * 비교식은 세 토큰이 모여야 뜻이 된다. 그냥 단어로 끊으면 "재알림 14.6분" / "< 60분"
 * 처럼 부등호가 다음 줄로 넘어가거나 "재알림 5.0분 <" 처럼 줄 끝에 혼자 매달린다.
 * 둘 다 그 줄만 봐서는 무엇과 무엇을 비교하는지 읽을 수 없다.
 */
function atoms(segment: string): string[] {
  const words = segment.split(/\s+/).filter(Boolean);
  const out: string[] = [];
  for (let i = 0; i < words.length; i += 1) {
    if (OPERATOR.test(words[i]) && out.length > 0 && i + 1 < words.length) {
      out[out.length - 1] = `${out[out.length - 1]} ${words[i]} ${words[i + 1]}`;
      i += 1;
    } else {
      out.push(words[i]);
    }
  }
  return out;
}

/**
 * 긴 Detail 을 ThreeLineStack 이 이미 갖고 있는 줄에 나눠 담는다.
 *
 * 스킵 사유는 통째로 1줄에 들어가고 2·3줄이 비어 있었다. 그래서 폭을 넘기면
 * "..." 로 잘렸고, 잘리는 부분이 대개 숫자라 그 줄이 무의미해졌다
 * ("10%→20% gap 93.3m < 2h" → "10%→20% gap 93.3m <..."). 줄바꿈으로 행을
 * 키우는 대신 남는 줄에 분산한다.
 *
 * 어디서 끊느냐가 중요하다. 단순히 1줄을 폭까지 꽉 채우고 나머지를 흘리면
 * "단기 급등 · 재알림 14.6분" / "< 60분" 이 된다 -- 3줄 중 한 줄이 비어 있는데도
 * 비교식이 두 줄로 잘린 것이다. 그래서 (1) 모니터가 실제로 쓰는 의미 경계인
 * " · " 에서 먼저 끊고, (2) 그 안에서는 비교식을 한 덩어리로 유지한다.
 * 결과: "단기 급등 ·" / "재알림 14.6분 < 60분".
 *
 * 행 높이는 절대 늘리지 않는다 -- 줄바꿈(white-space:normal)을 쓰면 브로커 표들이
 * 행 높이를 맞춰 쓰는 전제가 깨지므로, 있는 3줄 안에서만 해결한다.
 * maxLines 로도 모자라면 마지막 줄이 나머지를 전부 갖는다(글자를 버리진 않는다).
 * 실제 Detail 들이 3줄에 들어가는지는 테스트가 지킨다.
 */
export function wrapDetail(detail: string, maxLines = 3): string[] {
  const segments = detail.split(' · ').filter((s) => s.length > 0);
  if (segments.length === 0) return [];
  // 구분자는 앞 구절 끝에 남긴다 -- 줄이 바뀌어도 "뒤에 더 있다"가 보인다.
  const groups = segments.map((seg, i) => {
    const a = atoms(seg);
    if (a.length > 0 && i < segments.length - 1) a[a.length - 1] += ' ·';
    return a;
  });

  const lines: string[] = [];
  let cur = '';
  groups.forEach((group, gi) => {
    // 다음 구절이 지금 줄에 통째로는 안 들어가면, 구절 경계에서 줄을 바꾼다.
    // 단어 중간이 아니라 의미 단위로 끊기 위한 것.
    if (cur && gi > 0 && lines.length < maxLines - 1
        && textUnits(`${cur} ${group.join(' ')}`) > DETAIL_LINE_UNITS) {
      lines.push(cur);
      cur = '';
    }
    group.forEach((atom) => {
      const next = cur ? `${cur} ${atom}` : atom;
      if (cur && textUnits(next) > DETAIL_LINE_UNITS && lines.length < maxLines - 1) {
        lines.push(cur);
        cur = atom;
      } else {
        cur = next;
      }
    });
  });
  if (cur) lines.push(cur);
  return lines;
}

/** 업숏·빗숏 path exit 다리 이름. monitor 의 PATH_EXIT_LEG_KO 와 **같은 표**다
 *  -- 갈라지면 같은 사건이 화면과 텍스트 리포트에서 다른 이름으로 보인다. */
const PATH_EXIT_LEG_KO: Record<string, string> = {
  main: u('본다리'),
  early: u('조기'),
  late_surge: u('후반급등'),
  v4miss: u('놓친고점'),
  grind: u('눌림지속'),
  bh_path_reclaim: u('고점탈환'),
  bh_path_stuck: u('정체'),
};

/** path exit 으로 끊은 EXIT 행의 사유. 다리 이름만으로는 "그 문턱을 어떻게
 *  넘었나" 를 못 읽으므로 판정에 실제로 쓰인 세 값을 같이 낸다:
 *  역행(고점까지 얼마나 밀렸나) · 회복(그 고점에서 얼마나 되돌아왔나) ·
 *  고점 나이(그 고점이 몇 분 전인가). 규칙은 H분 경과 **첫 폴에서 1회만**
 *  판정하므로, 이 세 값이 그 한 순간의 스냅샷이라는 점이 중요하다. */
export function pathExitDetail(exit: BinanceShortLiveEvent | null): string {
  const pe = exit?.path_exit;
  if (!pe) return '';
  const parts: string[] = [PATH_EXIT_LEG_KO[pe.leg ?? 'main'] ?? (pe.leg ?? 'main')];
  if (pe.adv_pct != null && Number.isFinite(pe.adv_pct)) {
    parts.push(`역행 ${pe.adv_pct}%`);
  }
  if (pe.recovery_pct != null && Number.isFinite(pe.recovery_pct)) {
    parts.push(`회복 ${pe.recovery_pct}%`);
  }
  if (pe.peak_age_min != null && Number.isFinite(pe.peak_age_min)) {
    parts.push(`고점 ${pe.peak_age_min}분전`);
  }
  if (pe.elapsed_min != null && Number.isFinite(pe.elapsed_min)) {
    parts.push(`${pe.elapsed_min}분`);
  }
  return `경로청산 ${parts.join(' · ')}`;
}

/** 편승 pr_settle 탈출 사유. Detail 칸은 진입 detail 만 보여 주므로 exit 쪽을
 *  따로 적는다 (monitor detail_text 와 같은 한글 라벨). */
const PR_SETTLE_REASON_KO: Record<string, string> = {
  soft_stop: u('소프트'),
  hard_stop: u('하드'),
  trail: u('트레일'),
  time: u('만기'),
  manual: u('수동'),
  external_close: u('외부청산'),
};

const LIQ_SETTLE_REASON_KO: Record<string, string> = {
  hold: u('홀드 만기'),
  extend_cap: u('연장 만기'),
  recovered: u('회복 조기청산'),
  tp: u('+5% 익절'),  // v3 TP5 — TP 약어는 안 씀 (2026-10-31)
  external: u('외부청산'),
  liquidation: u('강제청산'),
};

/** 청캐 v2/v3 정산 Detail 보조. Detail 본문은 진입 행이라, 정산의
 *  연장·사유·조각을 여기서 붙인다. v3 만 빠져 있으면 연장 정산인데도
 *  화면에 "연장" 이 안 보인다 (2026-10-31 AIN 11:44). */
export function liqv2ExitDetail(exit: BinanceShortLiveEvent | null): string {
  if (!exit) return '';
  const v2 = exit.action === 'liqv2_settle';
  const v3 = exit.action === 'liqv3_settle';
  if (!v2 && !v3) return '';
  const parts: string[] = [];
  const extended = Boolean(exit.extended)
    || exit.reason === 'recovered'
    || exit.reason === 'extend_cap';
  if (extended) parts.push(u('연장'));
  if (v3 && exit.reason) {
    const ko = LIQ_SETTLE_REASON_KO[exit.reason];
    if (ko) parts.push(ko);
  }
  if (exit.legs != null && Number.isFinite(exit.legs)) {
    const ml = exit.max_legs;
    parts.push(
      ml != null && Number.isFinite(ml)
        ? `조각 ${exit.legs}/${ml}`
        : `조각 ${exit.legs}`,
    );
  }
  return parts.join(' · ');
}

/** 겹롱 gl_settle 탈출 사유. Detail 칸은 진입 문장만 보여 주므로 청산은 따로 붙인다.
 *  문장은 모니터 `exit_why` (+고점%→+청산%). 옛 페이로드는 reason 만 있을 수 있다. */
const GL_SETTLE_REASON_KO: Record<string, string> = {
  stop: u('스탑'),
  trail: u('트레일'),
  time: u('만기'),
};

export function glSettleExitDetail(exit: BinanceShortLiveEvent | null): string {
  if (!exit || exit.action !== 'gl_settle') return '';
  if (exit.exit_why) return exit.exit_why;
  if (exit.reason && !(exit.manual && exit.reason === 'manual')) {
    return GL_SETTLE_REASON_KO[exit.reason] ?? exit.reason;
  }
  return '';
}

export function prSettleExitDetail(exit: BinanceShortLiveEvent | null): string {
  if (!exit || exit.action !== 'pr_settle') return '';
  const why = exit.reason
    ? (PR_SETTLE_REASON_KO[exit.reason] ?? exit.reason)
    : (() => {
        // 배포 전 구행: detail "+1.2 (+3.4%) USDT · 소프트 [수동]" 에서 꼬리만
        const m = exit.detail.match(/USDT(?:\s·\s([^[\n]+))?/);
        return (m?.[1] ?? '').trim();
      })();
  const parts: string[] = [];
  if (why && !(exit.manual && why === '수동')) parts.push(why);
  if (exit.held_min != null && Number.isFinite(exit.held_min)) {
    parts.push(`${exit.held_min}분`);
  }
  if (exit.mfe_pct != null && Number.isFinite(exit.mfe_pct)) {
    const sign = exit.mfe_pct >= 0 ? '+' : '';
    parts.push(`MFE ${sign}${exit.mfe_pct}%`);
  }
  return parts.join(' · ');
}

export function normalizeEventType(ev: BinanceShortLiveEvent): string {
  if (isIntakeSkip(ev) || ev.action.startsWith('skip_')) return 'SKIP';
  return ev.type;
}

/** 숏의 settle 과 정확히 같은 time_kst 에 같은 심볼/퍼프로 열린 long_entry 를 찾는다
 *  -- open_abort_long() 이 그 숏을 정산하는 바로 그 틱에서 불리므로 이 동시성이
 *  abort→long 을 다른 롱(0건이지만 flip 유래)과 구분하는 유일한 신뢰 가능한 신호다.
 *  sameTrade() 의 lane 체크는 여기 쓰지 않는다 -- long_entry 액션 이름은 원본이
 *  빗썸 슬리브였어도 항상 'long_entry'(bh_ 접두 없음)라 레인이 안 갈린다. */
/** 재숏 다리. **롱 다리 뒤에** 찾는다 -- 재숏은 abort->롱 이 닫힌 뒤에 열린다. */
function findReshortLeg(
  sorted: BinanceShortLiveEvent[],
  used: Set<number>,
  headIdx: number,
  head: BinanceShortLiveEvent,
): BinanceAlertRow['reshortLeg'] {
  // **sameTrade 로 본다 -- 심볼만 보면 안 된다.** 처음엔 symbol+perp 만 봤는데,
  // 하루 전 후보-롱 감시행(cl_candidate, 같은 심볼)이 forEach 순서상 먼저 처리되며
  // 재숏을 가로챘다(MOVE: 08-21 21:33 감시행이 08-22 13:02 재숏을 집어갔다).
  // sameTrade 는 레인까지 보므로 cl 레인 행은 upbit 재숏을 못 집는다.
  // 그리고 **자기 머리 뒤**에서만 찾는다 -- 재숏은 그 알림의 abort 뒤에 온다.
  const idx = sorted.findIndex((e, j) => j > headIdx && !used.has(j)
    && e.action === 'reshort' && sameTrade(head, e));
  if (idx === -1) return null;
  used.add(idx);
  const cand = sorted[idx];
  let exitIdx = -1;
  for (let k = idx + 1; k < sorted.length; k += 1) {
    if (used.has(k)) continue;
    const c2 = sorted[k];
    if (c2.action !== 'settle' && c2.action !== 'bh_settle') continue;
    if (c2.symbol !== cand.symbol || c2.perp !== cand.perp) continue;
    exitIdx = k;
    break;
  }
  if (exitIdx !== -1) used.add(exitIdx);
  return { entry: cand, exit: exitIdx !== -1 ? sorted[exitIdx] : null };
}

function findAbortLongLeg(
  sorted: BinanceShortLiveEvent[],
  used: Set<number>,
  shortExit: BinanceShortLiveEvent | null,
): BinanceAlertRow['longLeg'] {
  // 2026-08-08: 여기가 'settle' 만 받고 있었다. abort→long 은 abort_long_legs
  // (oi_early/px/px_cont) 로 **빗썸 슬리브에서도** 열리는데 그 레인의 청산 액션은
  // 'bh_settle' 이라, 슬리브발 롱은 이 함수가 시작도 못 하고 null 을 돌려줬다 --
  // 청산된 뒤에는 화면 어디에도 안 남는다(보유 블록은 열린 것만 그린다).
  // KRW-BICO 08-08T15:20:20Z (+2.988%, +$14.98) 가 그 경우였고, 같은 날 리플레이
  // 에서 abort 10건 중 8건이 슬리브였으니 이게 다수 경로다.
  // 위 주석이 "롱 쪽은 레인이 안 갈린다" 까지는 알고 있었는데 숏 쪽은 갈린다는
  // 것(settle vs bh_settle)을 빠뜨렸다.
  if (!shortExit || (shortExit.action !== 'settle' && shortExit.action !== 'bh_settle')) {
    return null;
  }
  const entryIdx = sorted.findIndex((e, j) => !used.has(j)
    && e.action === 'long_entry'
    && e.symbol === shortExit.symbol && e.perp === shortExit.perp
    && e.time_kst === shortExit.time_kst);
  if (entryIdx === -1) return null;
  used.add(entryIdx);
  const cand = sorted[entryIdx];
  let exitIdx = -1;
  for (let k = entryIdx + 1; k < sorted.length; k += 1) {
    if (used.has(k)) continue;
    const c2 = sorted[k];
    if (c2.action !== 'long_settle') continue;
    if (c2.symbol !== cand.symbol || c2.perp !== cand.perp) continue;
    exitIdx = k;
    break;
  }
  if (exitIdx !== -1) used.add(exitIdx);
  return { entry: cand, exit: exitIdx !== -1 ? sorted[exitIdx] : null };
}

/** 행 키. **signal_id 를 반드시 섞는다** (2026-08-20).
 *
 *  예전 키는 `time_kst-symbol-action` 이었는데, 같은 알림이 여러 번 들어오면
 *  셋이 전부 같아 **키가 충돌한다**. 재전송은 실제로 생긴다: 서버가 밀리면 폰이
 *  큐에 쌓아 두고 다시 쏘고, signal_id 가 전송마다 새로 생겨 DB 에 여러 행이 된다
 *  (2026-08-19 KRW-RE 19:25:23Z 한 건이 6행). 그날 페이로드에 중복 키가 17조였다.
 *
 *  키가 겹치면 React 재조정이 깨진다 -- "진입(예정)" 스위치를 껐다 켤 때마다
 *  그 행들이 사라지지 않고 **두 배로 늘었다**(유저 보고). 필터는 정상이었고
 *  (실제 코드에 실제 페이로드를 태워 통과 0행 확인) 키만 문제였다.
 *
 *  index 를 쓰지 않는 이유: 목록이 필터로 바뀌면 같은 행의 index 가 달라져
 *  재조정이 또 헛돈다. signal_id 는 그 행의 정체라 순서와 무관하다.
 */
function rowKey(ev: BinanceShortLiveEvent): string {
  return `${ev.time_kst}-${ev.symbol}-${ev.action}-${ev.signal_id ?? ''}`;
}

/** 쌍숏이 꺼져 있으면 업숏·빗숏 행에 붙이면 안 되는 결말. */
function isDualShortDisplayAction(action: string): boolean {
  return action.startsWith('ds_')
    || action === 'skip_ds_yield'
    || action === 'bh_skip_upbit_handled';
}

export function groupBinanceAlertRows(
  events: BinanceShortLiveEvent[],
  opts?: { dualShortLive?: boolean },
): BinanceAlertRow[] {
  // 인자를 안 주면 예전과 같이 접는다. 화면은 쌍숏 OFF 일 때 false 를 넘긴다.
  const dsOn = opts?.dualShortLive !== false;
  // 2026-10-26 유저: Historique 에서 쌍숏(ds_*) 행·접힘을 전부 숨긴다. 쌍숏은
  // 업숏·빗숏 종속 경로라 독립 행으로 안 띄운다(손익은 부모 귀속 그대로).
  const sorted = [...events]
    .filter((e) => !(e.action || '').startsWith('ds_')
      && (e.action || '') !== 'skip_ds_yield')
    .sort((a, b) => a.time_kst.localeCompare(b.time_kst));
  const used = new Set<number>();
  const rows: BinanceAlertRow[] = [];

  const takeNext = (
    startIdx: number,
    alert: BinanceShortLiveEvent,
    predicate: (ev: BinanceShortLiveEvent) => boolean,
    // 여기서 스캔을 멈춘다(그 행도 안 집어간다). sid 없는 옛 결말을 시간순으로만
    // 이을 때, **그 사이에 낀 새 알림**을 넘어가 버리는 것을 막는 울타리다.
    stopAt?: (ev: BinanceShortLiveEvent) => boolean,
    match: (a: BinanceShortLiveEvent, b: BinanceShortLiveEvent) => boolean = sameTrade,
  ): BinanceShortLiveEvent | null => {
    for (let j = startIdx + 1; j < sorted.length; j += 1) {
      if (used.has(j)) continue;
      const next = sorted[j];
      if (!match(alert, next)) continue;
      if (stopAt?.(next)) return null;
      if (predicate(next)) {
        used.add(j);
        return next;
      }
    }
    return null;
  };

  // 같은 알림 창 안에서 결말이 둘이면 **실진입을 SKIP 보다 우선**한다
  // (2026-08-25 KRW-VVV). skip_no_pullback 뒤 대시보드 수동진입이 short 를
  // 찍어도, 첫 결말만 집으면 행이 영영 SKIP 이고 OPEN 하이라이트도 안 된다.
  // 울타리(stopAt)·sid 매칭은 그대로 — KRW-ONG 의 "앞 알림이 뒤 short 훔치기"
  // 는 막힌 채다.
  const takePreferredNext = (
    startIdx: number,
    alert: BinanceShortLiveEvent,
    predicate: (ev: BinanceShortLiveEvent) => boolean,
    prefer: (ev: BinanceShortLiveEvent) => boolean,
    stopAt?: (ev: BinanceShortLiveEvent) => boolean,
    match: (a: BinanceShortLiveEvent, b: BinanceShortLiveEvent) => boolean = sameTrade,
  ): BinanceShortLiveEvent | null => {
    let skipIdx = -1;
    let preferIdx = -1;
    for (let j = startIdx + 1; j < sorted.length; j += 1) {
      if (used.has(j)) continue;
      const next = sorted[j];
      if (!match(alert, next)) continue;
      if (stopAt?.(next)) break;
      if (!predicate(next)) continue;
      if (prefer(next)) {
        if (preferIdx < 0) preferIdx = j;
      } else if (skipIdx < 0) {
        skipIdx = j;
      }
    }
    const chosen = preferIdx >= 0 ? preferIdx : skipIdx;
    if (chosen < 0) return null;
    used.add(chosen);
    // 이긴 실진입이 있으면 앞선 SKIP 도 소비한다 — 안 그러면 미사용
    // skip_no_pullback 이 아래에 남아 조용히 버려지긴 하지만, used 를 맞춰
    // 같은 sid 재스캔이 그 SKIP 을 다시 집지 않게 한다.
    if (preferIdx >= 0 && skipIdx >= 0 && skipIdx < preferIdx) used.add(skipIdx);
    return sorted[chosen];
  };

  sorted.forEach((ev, i) => {
    if (used.has(i)) return;

    if (!dsOn && isDualShortDisplayAction(ev.action)) {
      used.add(i);
      return;
    }

    if (isIntakeSkip(ev)) {
      used.add(i);
      rows.push({
        key: rowKey(ev),
        alert: ev,
        entry: ev,
        exit: null,
        longLeg: null,
        reshortLeg: null,
        addon: null,
      });
      return;
    }

    if (!ALERT_ACTIONS.has(ev.action)) return;

    // 쌍숏 감시행: 부모 알림(업 signal / 빗 bh_signal)이 있으면 **단독 행을
    // 만들지 않는다** (2026-09-06). Ex 를 업으로 위장하지 않고, 결말만 부모
    // 행에 접는다. 「필요한」ON + 빗숏 OFF 면 빗 알림 행은 원래 접힌다.
    if (ev.action === 'ds_candidate' && hasDsParentAlert(sorted, ev)) {
      used.add(i);
      return;
    }

    used.add(i);
    // 청산 레인은 머리가 곧 진입이다(알림이 없다). 나머지 레인은 알림 뒤에서 찾는다.
    // 청산 레인과 후보-롱은 머리가 곧 진입이다(알림 행에 붙지 않는다).
    // 후보-롱 감시행은 **결말을 자기 행 안에서** 받는다 (2026-08-14).
    // 감시(cl_candidate)와 그 결말(cl_entry / cl_skip_no_trigger)은 액션 이름이
    // 달라 예전엔 두 행으로 갈라졌고, 그래서 트리거돼 진입까지 간 뒤에도 감시행이
    // 영원히 'WATCH' 로 남았다(유저 지적: "03:56 건이 아직도 WATCH" -- 실제로는
    // 04:09 에 진입했다). 백엔드가 두 행에 같은 `signal_id` 를 싣는다.
    const parentTakesDs = dsOn && (ev.action === 'signal' || ev.action === 'bh_signal');
    let entry = (ev.action === 'liq_entry' || ev.action === 'cl_entry'
      || ev.action === 'liqv2_entry' || ev.action === 'liqv2_add'
      || ev.action === 'liqv3_entry'
      || ev.action === 'gl_entry' || ev.action === 'gl_entry_fail')
      ? ev
      // 청캐 감시행은 후보-롱과 **같은 방식**으로 결말을 자기 행 안에 받는다.
      // 안 그러면 한 거래가 WATCH 행 + ENTRY 행 둘로 갈라지고, 감시행은 영원히
      // WATCH 로 남는다(cl 레인이 08-14 에 정확히 이 사고를 냈다).
      // 다음 감시행에서 멈추는 것도 같은 이유다: 같은 심볼에 트리거가 연달아
      // 붙으면 먼저 온 감시가 나중 감시의 진입을 훔친다(KRW-EDEN).
      : ev.action === 'liq_watch'
        ? takePreferredNext(i, ev, (next) => (
          // v2 사다리 감시는 liqv2_entry 로 진입한다 (liq_entry 아님) —
          // 안 넣으면 WATCH 행 + SHORT/OPEN 행 둘로 갈라진다 (2026-09-10 유저).
          (next.action === 'liq_entry' || next.action === 'liqv2_entry'
            || next.action === 'liq_skip')
          && (!ev.signal_id || !next.signal_id || next.signal_id === ev.signal_id)
        ), (next) => next.action === 'liq_entry' || next.action === 'liqv2_entry',
        (next) => next.action === 'liq_watch')
      : ev.action === 'liq_skip'
        ? takePreferredNext(i, ev, (next) => (
          (next.action === 'pr_entry'
            || next.action === 'pr_entry_fail'
            || next.action === 'pr_trigger_paper')
          && next.source === 'liq_kr_skip'
          && (!ev.signal_id || !next.signal_id || next.signal_id === ev.signal_id)
        ), (next) => next.action === 'pr_entry',
        (next) => next.action === 'liq_skip' || next.action === 'liq_watch')
      : ev.action === 'cl_candidate'
        ? takePreferredNext(i, ev, (next) => (
          (next.action === 'cl_entry' || next.action.startsWith('cl_skip_'))
          // signal_id 가 양쪽에 있으면 그것으로 못박는다. 옛 이벤트(그 필드가
          // 없던 시절)는 sameTrade + 시간순으로만 잇는다 -- 없다고 안 잇는 것보다
          // 낫다(그러면 화면이 다시 WATCH 로 멈춘다).
          && (!ev.signal_id || !next.signal_id || next.signal_id === ev.signal_id)
        ), (next) => next.action === 'cl_entry',
        // **다음 감시행에서 멈춘다** (2026-08-14). 위 시간순 폴백은 sid 없는
        // 결말을 아무 감시행이나 집어가게 둬서, 같은 심볼에 감시가 연달아
        // 붙으면 **먼저 온 감시가 나중 감시의 진입을 훔친다**. 실제로 KRW-EDEN
        // 에서 04:49 감시행이 06:10 감시행의 06:52 진입(+07:09 정산)을 가져가,
        // 진짜 주인인 06:10 행은 결말 없이 비어 보였다. 감시가 겹칠 수 있는 건
        // 창이 113분인데 알림이 그보다 자주 오기 때문이다 -- 정상 상태다.
        (next) => next.action === 'cl_candidate')
      : ev.action === 'pr_candidate'
        ? takePreferredNext(i, ev, (next) => (
          (next.action === 'pr_entry' || next.action === 'pr_trigger_paper'
            || next.action === 'pr_skip' || next.action === 'pr_entry_fail')
          && (!ev.signal_id || !next.signal_id || next.signal_id === ev.signal_id)
        ), (next) => next.action === 'pr_entry')
        // pr_candidate 에 stopAt 을 두지 않는다. 같은 perp 에 감시가 여러
        // 개(06:29·07:07·07:31 ONG)일 때 cl 처럼 "다음 pr_candidate 에서
        // 멈춤" 은 **sid 가 다른 중간 감시**에서 스캔이 끊겨, 뒤쪽 pr_entry
        // (10:42) 가 orphan 이 되고 진입 시각이 ALERT 로 뜬다. signal_id
        // 매칭이 훔치기를 막는다 — cl_candidate 의 stopAt 과 다른 함정.
      : ev.action === 'ds_candidate'
        // 부모 알림이 없을 때만 단독 머리 (위 swallow). 결말은 자기 행에.
        ? takePreferredNext(i, ev, (next) => (
          (next.action === 'ds_short' || next.action === 'ds_skip'
            || next.action === 'ds_entry_fail')
          && (!ev.signal_id || !next.signal_id || next.signal_id === ev.signal_id)
        ), (next) => next.action === 'ds_short')
      : ev.action === 'gl_watch'
        // 대기가 머리다. 15초 안 진입은 이 행에 붙고, 다음 대기가 앞 대기의
        // 진입을 가져가지 않는다.
        // 다음 대기에서 끊지 않는다. 앞 대기의 SKIP 은 다음 대기가 찍힌
        // 뒤에 온다. signal_id 가 주인이다.
        ? takePreferredNext(i, ev, (next) => (
          (next.action === 'gl_entry' || next.action === 'gl_entry_fail'
            || next.action === 'gl_skip')
          && (!ev.signal_id || !next.signal_id || next.signal_id === ev.signal_id)
        ), (next) => next.action === 'gl_entry')
        : takePreferredNext(i, ev, (next) => (
          (ENTRY_OUTCOME_ACTIONS.has(next.action)
            && (dsOn || !isDualShortDisplayAction(next.action)))
          || (parentTakesDs && (
            next.action === 'ds_short' || next.action === 'ds_entry_fail'
            || (next.action === 'ds_skip' && !DS_PRE_WATCH_SKIP_REASONS.has(next.reason ?? ''))
          ))
        ),
        (next) => isPositionEntry(next.action),
        // signal_id 가 있으면 다음 알림에서 끊지 않는다 — sid 매칭이 훔치기를
        // 막고, 중간 알림(linear 스케일 트리거) 너머의 short 를 주인 행에 붙인다
        // (2026-09-07 IOST 15:14→16:02). sid 없는 옛 이벤트만 stopAt 유지.
        ev.signal_id
          ? undefined
          : ((next) => next.action === 'signal' || next.action === 'bh_signal'),
        parentTakesDs ? sameTradeOrDsChildPreferSignalId : sameTradePreferSignalId);
    // 부모 행이 쌍숏 결말을 집었으면 같은 심볼 ds_candidate 도 소비(중복 행 방지).
    if (parentTakesDs && entry?.action.startsWith('ds_')) {
      for (let j = 0; j < sorted.length; j += 1) {
        if (used.has(j)) continue;
        const c = sorted[j];
        if (c.action === 'ds_candidate' && dsChildOfParent(ev, c)) used.add(j);
      }
    }
    const exitAction = entry ? EXIT_FOR_ENTRY[entry.action] : undefined;
    // EXIT 는 stopAt 을 쓰지 않는다 (2026-08-25 KRW-PROM). 진입 묶기만 다음
    // 알림에서 멈추면 된다 -- 정산은 그 **다음** 알림 뒤에 올 수 있다(12:20
    // bh_signal → 12:47 진입 → 13:24 재알림 → 13:52 bh_settle). stopAt 을
    // exit 에도 쓰면 정산이 null 이 되어 실현 PnL 칸이 '–' 로 비었다.
    // liq_skip 머리에 pr_entry(liq_kr_skip)가 붙은 행은 같은 사슬의 EXIT(pr_settle)를
    // entry 기준으로 찾아야 한다 -- alert 기준(lane=liq)으로는 같은 거래로 못 묶인다.
    const exitHead = entry ?? ev;
    // linear 스케일·청캐 v2 조각: 본진입/첫 조각이 settle 을 이미 used 로
    // 집어도 **같은 청산**을 다시 쓴다 (모니터 attach_entry_charts 와 동일).
    // used 전용이면 뒤 조각 행만 열린 것처럼 live 마크가 붙는다.
    const exit = exitAction
      ? (entry?.action === 'addon_short'
        || entry?.action === 'liqv2_add'
        || entry?.action === 'liqv2_entry'
        ? (() => {
          const wantSid = entry.signal_id || (entry as { sid?: string }).sid;
          for (let j = i + 1; j < sorted.length; j += 1) {
            const next = sorted[j];
            if (next.action !== exitAction) continue;
            if (next.perp !== exitHead.perp) continue;
            if ((next.time_kst || '') <= (exitHead.time_kst || '')) continue;
            if (exitHead.symbol && next.symbol && next.symbol !== exitHead.symbol) {
              continue;
            }
            // v2: sid 가 있으면 같은 슬리브만 (다른 슬리브 settle 훔치기 방지).
            if (wantSid) {
              const nextSid = next.signal_id || (next as { sid?: string }).sid;
              if (nextSid && nextSid !== wantSid) continue;
            }
            if (!used.has(j)) used.add(j);
            return next;
          }
          return null;
        })()
        : entry?.action === 'liqv3_entry'
          // v3: 같은 종목에 다리(L1·L2)가 겹친다. 시간순으로 집으면 앞 진입이
          // 뒤 다리 정산을 가져간다 (2026-10-27 AIN: 16:00 L1 에 21:59 L2 +49.76,
          // 23:45 L2 에 00:00 L1 −353). sid 같은 정산만. 원장 보정(cash complete)이
          // 같은 sid 로 한 번 더 오므로 둘 다 소비하고 확정본을 보인다.
          ? (() => {
            const wantSid = entry.signal_id || (entry as { sid?: string }).sid;
            if (!wantSid) return takeNext(i, exitHead, (next) => next.action === exitAction);
            let hit: BinanceShortLiveEvent | null = null;
            // 조각(2026-10-28 v2 진입방식): 같은 sid 진입 행이 여럿 — 정산 하나를
            // 같이 쓴다. used 로 거르면 첫 조각만 닫히고 나머지는 열린 채 남는다.
            for (let j = i + 1; j < sorted.length; j += 1) {
              const next = sorted[j];
              if (next.action !== exitAction || next.perp !== exitHead.perp) continue;
              const nextSid = next.signal_id || (next as { sid?: string }).sid;
              if (nextSid !== wantSid) continue;
              used.add(j);
              if (!hit || next.cash_complete) hit = next;
            }
            return hit;
          })()
          : takeNext(i, exitHead, (next) => next.action === exitAction))
      : null;
    const stopAtNextAlert = (next: BinanceShortLiveEvent) =>
      next.action === 'signal' || next.action === 'bh_signal'
      || next.action === 'cl_candidate' || next.action === 'liq_watch';

    // 청캐 모드 전환(liq_mode)을 WATCH 행에 접는다 (2026-09-04).
    // 안 접으면 entry_mode 가 최초 liq_watch 값에 굳어 WATCH(fade)로
    // 바뀐 뒤에도 라벨이 안 따라온다. 마지막 to 가 최신 모드.
    let alertEv = ev;
    if (ev.action === 'liq_watch') {
      let lastMode: BinanceShortLiveEvent | null = null;
      for (let j = i + 1; j < sorted.length; j += 1) {
        const next = sorted[j];
        if (!sameTrade(ev, next)) continue;
        // 진입/스킵/다음 감시 뒤의 모드는 무시. used 여부와 무관하게
        // break -- entry 가 이미 used 면 continue 로 건너뛰어 뒤 모드를
        // 잘못 접는다.
        if (next.action === 'liq_watch') break;
        if (next.action === 'liq_entry' || next.action === 'liq_skip') break;
        if (used.has(j)) continue;
        if (next.action !== 'liq_mode') continue;
        used.add(j);
        lastMode = next;
      }
      if (lastMode) {
        const mode = lastMode.to || lastMode.entry_mode;
        alertEv = {
          ...ev,
          entry_mode: mode || ev.entry_mode,
          watch_min: lastMode.watch_min ?? ev.watch_min,
        };
      }
    }

    // 빗숏 스킵은 새 행이 아니다. 같은 알림의 두 번째 줄이 그 시각의 IGNORÉ 다.
    // 부모가 없는 인테이크(구간 하한 등)만 아래에서 단독 행이 된다.
    if (ev.action === 'bh_signal' && !entry) {
      for (let j = i + 1; j < sorted.length; j += 1) {
        if (used.has(j)) continue;
        const next = sorted[j];
        if (!next.action.startsWith('bh_skip_')) continue;
        if (!sameTradePreferSignalId(ev, next)) continue;
        used.add(j);
        const note = bhSkipFoldNote(next);
        const base = (alertEv.detail || '').trim();
        const detail = note && !base.includes(note)
          ? (base ? `${base} · ${note}` : note)
          : alertEv.detail;
        alertEv = { ...alertEv, detail };
        entry = { ...next, detail: '' };
        break;
      }
    }

    rows.push({
      key: rowKey(ev),
      alert: alertEv,
      entry,
      exit,
      longLeg: findAbortLongLeg(sorted, used, exit),
      // **longLeg 뒤에 부른다** -- used 를 공유하므로 순서가 곧 사슬 순서다.
      reshortLeg: findReshortLeg(sorted, used, i, ev),
      // classic 추가진입(같은 알림 사슬)만 본진입 행에 붙인다.
      // linear_rung 은 **트리거 알림 행의 entry** 로 따로 붙인다(위에서
      // ENTRY_OUTCOME). 여기 다시 집으면 두 행이 비거나 차트·진입표시가
      // 한쪽에만 모인다 (2026-09-07 IOST — 유저: 행을 묶을 필요 없다).
      addon: (entry && entry.action === 'short')
        ? takeNext(i, ev, (next) => (
          next.action === 'addon_short' && next.mode !== 'linear_rung'
        ), stopAtNextAlert,
        (alert, next) => {
          if (next.action !== 'addon_short' || next.mode === 'linear_rung') {
            return false;
          }
          if (!sameTrade(alert, next)) return false;
          if (alert.signal_id && next.signal_id) {
            return alert.signal_id === next.signal_id;
          }
          return true;
        })
        : null,
    });
  });

  // 빗 알림이 결말 없이 WATCH 로 남는 경우: 같은 perp 업 signal 에 이미
  // ds_short 가 접혀 있으면 SKIP「[쌍숏진입] 업숏에서처리」(2026-09-06 IOST).
  // 라이브가 bh_skip_upbit_handled 를 아직 안 찍은 옛 이벤트·배포 전 구간용.
  const dsOnUpbitByPerp = new Map<string, BinanceShortLiveEvent>();
  for (const r of rows) {
    if (r.entry?.action !== 'ds_short' || !r.alert.perp) continue;
    // attr_lane 없거나 upbit → 업숏 쪽 귀속. bh 면 빗이 주인.
    if (r.entry.attr_lane === 'bh') continue;
    dsOnUpbitByPerp.set(r.alert.perp, r.entry);
  }
  if (dsOn && dsOnUpbitByPerp.size > 0) {
    for (let i = 0; i < rows.length; i += 1) {
      const r = rows[i];
      if (r.alert.action !== 'bh_signal' || r.entry) continue;
      const ds = dsOnUpbitByPerp.get(r.alert.perp);
      if (!ds) continue;
      rows[i] = {
        ...r,
        entry: {
          ...r.alert,
          action: 'bh_skip_upbit_handled',
          type: 'SKIP',
          detail: u('[쌍숏진입] 업숏에서처리'),
          time_kst: ds.time_kst,
          time_hm: ds.time_hm,
          signal_id: r.alert.signal_id,
        },
      };
    }
  }

  return rows.sort((a, b) => b.alert.time_kst.localeCompare(a.alert.time_kst));
}

/** u("필요한 알림만") 켜질 때 숨길 행 — 거래 판단 전 파이프라인 노이즈. */
const NEEDED_ONLY_HIDDEN_ACTIONS = new Set(['skip_unresolved']);

/** 레인별 주문 스위치 — inactive 레인은 u("필요한") 에서 접는다(2026-08-25).
 *  cand_long_live / bh_newhigh_live / upbit_live / liq_cascade_live 는 **주문**
 *  여부이고 enabled 는 감시·기록 여부다 -- 화면 inactive 표시와 같은 축을 쓴다.
 *  (2026-08-27: 청캐도 live 로 분리. 옛 페이로드는 enabled 만 올 수 있어
 *  live 가 없으면 enabled 로 폴백.) */
export type NeededOnlyLaneLive = {
  upbit_live?: boolean;
  cand_long_live?: boolean;
  bh_newhigh_live?: boolean;
  liq_cascade_live?: boolean;
  /** @deprecated 2026-08-27 live 분리 전 페이로드. live 없을 때만 폴백. */
  liq_cascade_enabled?: boolean;
  pump_ride_live?: boolean;
  /** yaml pump_ride_exchanges. 있으면 그 거래소 pr_ 만 u("필요한")에 남긴다. */
  pump_ride_exchanges?: string[];
  /** 쌍숏(2026-09-02). 실주문과 같이 부모(업숏|빗숏) 중 하나라도 ON 일
   *  때만 ds_ 행을 "필요한"에 남긴다 — dual_short_live 단독 ON + 부모 둘 다
   *  OFF 면 감시·반사실은 돌아도 화면에서는 접는다 (2026-09-06). */
  dual_short_live?: boolean;
};

/** DB `live_lane_settings` 레인 id → NeededOnlyLaneLive 키.
 *  설정 토글이 정본이다(2026-09-05). config yaml 폴백만으로는 레인을 다 껐는데
 *  "필요한" 에 ALERT 가 남는 사고가 난다 — enrich 가 빠지거나 저장 직후
 *  config 가 아직 옛 값일 때. */
const LANE_SETTINGS_TO_LIVE_KEY: Record<string, keyof NeededOnlyLaneLive> = {
  upbit: 'upbit_live',
  bh: 'bh_newhigh_live',
  cand_long: 'cand_long_live',
  liq: 'liq_cascade_live',
  pump_ride: 'pump_ride_live',
  dual_short: 'dual_short_live',
};

/** lane_settings.lanes 가 있으면 그 live 로 fallback(config) 을 덮는다. */
export function mergeNeededOnlyLaneLive(
  fallback?: NeededOnlyLaneLive,
  lanes?: ReadonlyArray<{ id: string; live: boolean }> | null,
): NeededOnlyLaneLive {
  const out: NeededOnlyLaneLive = { ...(fallback ?? {}) };
  if (!lanes?.length) return out;
  for (const lane of lanes) {
    const key = LANE_SETTINGS_TO_LIVE_KEY[lane.id];
    if (key) {
      (out as Record<string, unknown>)[key] = lane.live;
    }
  }
  return out;
}

/**
 * 업비트 + 청산 + **후보-롱이 실제로 쓰는 빗썸 타입**만 남긴다. 수집 전용
 * (코인원, 그 밖의 빗썸 타입)과 심볼 미해석은 전략 필터가 아니라 인입 단계
 * 노이즈라 접는다.
 *
 * `candLongTypes` 는 백엔드 config 의 `cand_long_alert_types` 를 그대로 받는다 --
 * 화면이 목록을 재구현하면 yaml 을 바꿔도 화면이 안 따라간다(2026-08-10 청산
 * 노셔널 하드코딩과 같은 함정). 인자가 없으면 기본값으로 폴백하되, 그건
 * 백엔드가 값을 안 내려준 경우뿐이다.
 */
export function filterNeededOnlyRows(
  rows: BinanceAlertRow[],
  candLongTypes: string[] = ['segment_rise'],
  laneLive?: NeededOnlyLaneLive,
): BinanceAlertRow[] {
  const upLive = laneLive?.upbit_live ?? true;
  const clLive = laneLive?.cand_long_live ?? true;
  const bhLive = laneLive?.bh_newhigh_live ?? true;
  // 청캐: live 가 있으면 그것, 없으면 옛 enabled 폴백(배포 어긋남 대비).
  const liqLive = laneLive?.liq_cascade_live
    ?? laneLive?.liq_cascade_enabled
    ?? true;
  const prLive = laneLive?.pump_ride_live ?? true;
  const dsLive = laneLive?.dual_short_live ?? false;
  // 백엔드 dual_short_effective_live 와 같은 축: 부모 둘 다 OFF 면 체결 불가.
  const dsEffective = dsLive && (upLive || bhLive);

  // 주문 스위치가 전부 OFF 면 "필요한" 은 비어야 한다 (2026-09-05).
  // 개별 분기 구멍·기본값 true 폴백과 무관하게 한 줄로 못박는다.
  // 쌍숏은 dual_short_live 가 켜져 있어도 부모 OFF 면 effective OFF (2026-09-06).
  if (!upLive && !clLive && !bhLive && !liqLive && !prLive && !dsEffective) {
    return [];
  }

  return rows.filter((r) => {
    const ex = r.alert.exchange ?? 'upbit';
    // **바이낸스 퍼프가 없는 심볼은 아예 안 그린다** (2026-08-14 유저 지시).
    // 이 라인은 바이낸스 선물로만 거래하므로 퍼프가 없으면 어떤 레인도 칠 수
    // 없다 -- 판단이 아니라 인입 단계에서 끝난 건이다. 백엔드가 **알림 시점에**
    // 판정해 `no_perp` 로 실어준다(퍼프 부재를 화면이 추론하지 않는다: 애초에
    // perp 를 안 싣는 액션과 구분이 안 되기 때문이다).
    if (r.alert.no_perp) return false;

    const ln = lane(r.alert);
    // inactive 레인(up/cl_/bh_/pr_)은 감시·기록만 돌아도 "필요한" 에서는 노이즈다.
    // (유저: cand_long_live=false 인데 cl_candidate WATCH 가 남아 있다.)
    // 2026-08-26 에 살아 있는 cl 만 남기려다 같은 알림의 bh_skip 과 **두 줄로
    // 겹쳤다** — 끄면 숨기는 쪽이 맞다. 노란점·% 는 보이는 bh/업숏 행에 붙인다.
    if (ln === 'upbit' && !upLive) return false;
    if (ln === 'cl' && !clLive) return false;
    if (ln === 'bh' && !bhLive) return false;
    if (ln === 'pr' && !prLive) return false;
    // 쌍숏: dual_short_live AND (업숏|빗숏) — 빗숏 live OFF 때 bh_ 를 접는 것과
    // 같은 정신. 부모 둘 다 OFF 면 게이트·반사실은 돌아도 "필요한" 노이즈.
    // ex==='upbit'/'bithumb' 분기보다 먼저.
    if (ln === 'ds') return dsEffective;
    // 겹롱은 알림 거래소가 없다. exchange 기본값으로 업숏 분기에 넣지 않는다.
    if (ln === 'gl') return true;

    // 청산 캐스케이드. exchange='liq' 가 마지막 조건에서 false 가 되면
    // **통째로 걸러진다** (2026-08-09). 레인이 꺼져 있으면 같이 접는다.
    if (ex === 'liq') return liqLive;
    // 편승: yaml exchanges 화이트리스트 (2026-08-27 빗썸만).
    if (ln === 'pr') {
      const allowed = laneLive?.pump_ride_exchanges;
      if (allowed && allowed.length > 0 && !allowed.includes(ex)) return false;
      return true;
    }
    if (ex === 'upbit') return !NEEDED_ONLY_HIDDEN_ACTIONS.has(r.alert.action);
    // 후보-롱 레인(주문 ON 일 때만 여기까지 온다).
    if (ln === 'cl') return true;
    // 2026-08-13 (2차): 빗썸에서 남기는 타입이 **new_high 가 아니라
    // segment_rise** 다. 빗썸 페이드-숏은 이날 마스터 스위치까지 껐고
    // (bh_newhigh_sleeve_enabled: false), 후보-롱 모집단은 타입별 실측이
    // segment_rise 를 골랐다:
    //   segment_rise  발동 30.4% · t 1.84 · 최대기여 27%   <- 이 레인이 쓰는 타입
    //   new_high      발동  6.8% · t 1.10 · 최대기여 84%(한 건 의존)
    //   new_low       발동  0.9% · 사실상 미발동
    //   segment_fall  t -2.34 (음수)
    // 그래서 new_high 를 남기면 **거래하지 않는 알림**이 표를 채우고, 정작
    // 후보-롱이 보는 알림은 접힌다 -- 정확히 반대로 보이는 상태였다.
    //
    // 라이브의 단일 출처는 yaml `cand_long_alert_types` 다. 화면이 그 목록을
    // 쓰되, **빗썸 타입이라도 주문 스위치가 켜진 레인이 하나도 없으면** 접는다.
    if (ex === 'bithumb') {
      const ty = r.alert.alert_type ?? '';
      if (!candLongTypes.includes(ty)) return false;
      return clLive || bhLive;
    }
    return false;
  });
}

/** 대시보드에서 숨길 레인 id (설정·통계·알림 표). yaml/백엔드 페이로드는 그대로.
 *  dual_short: 단독 레인이 아니라 업숏·빗숏 종속이라 뺐다 (2026-09-04 유저).
 *  배분% 도 업숏 ON→업숏 / OFF→빗숏 을 따르고 파이 합산에 안 넣는다.
 *  편승(pump_ride)은 레인 표에 남긴다 (2026-10-20). 주문은 live false. */
export const DASHBOARD_HIDDEN_LANE_KEYS = new Set<string>(['cand_long', 'dual_short']);

/** 후보-롱 알림 행을 표에서 제거한다. 편승 행은 대시보드에 남긴다. */
export function filterDashboardHiddenLaneRows(rows: BinanceAlertRow[]): BinanceAlertRow[] {
  return rows.filter((r) => lane(r.alert) !== 'cl');
}

/** 진입 예정(WATCH) 시각 'HH:MM' 또는 null.
 *
 *  화면의 WATCH 배지(+마감)와 "진입(예정)" 필터의 **단일 출처**다 -- 두 곳이 각자
 *  계산하면 배지는 WATCH 인데 필터가 접는 행이 생긴다. 예정 시각 +3분까지만
 *  살아 있는 대기로 본다(그 뒤에도 진입 이벤트가 없으면 그 건은 조용히 사라진 것이라
 *  '예정' 이라고 부를 수 없다).
 *
 *  업비트 signal · 빗숏 bh_signal(되돌림 감시, watch_min) 이 대상이다. 청산 레인은
 *  알림이 없어(트리거가 곧 진입) 여기 안 온다. */
/** 청캐 감시창 기본값(분). 백엔드가 liq_watch 에 `watch_min` 을 실어주므로
 *  이건 그 필드가 없는 이벤트를 위한 폴백이다 -- **화면과 필터가 같은 값을
 *  써야** "보이는데 WATCH 가 아니다" 같은 어긋남이 안 생긴다. */
export const LIQ_WATCH_MIN_DEFAULT = 15;
export const CL_WATCH_MIN_DEFAULT = 113;
/** 편승 감시창 폴백(분). yaml pump_ride_watch_max_min · 이벤트 watch_min. */
export const PR_WATCH_MIN_DEFAULT = 180;
/** 쌍숏 되돌림 감시창 폴백(분). yaml dual_short_pullback_watch_min · 이벤트 watch_min. */
export const DS_WATCH_MIN_DEFAULT = 30;

/** 이 청캐 감시가 **아직 살아 있는가** (2026-08-22).
 *
 *  "진입(예정)" 스위치가 업비트 PENDING 을 남기는 것과 같은 자리다: 감시 중인
 *  트리거는 이 레인의 PENDING 이다(창이 15분이라 최대 몇 분 뒤에 주문이 나간다).
 *  빗썸 후보-롱 감시(cl_candidate)는 창이 113분이라 여기 안 넣는다 -- 그건 화면을
 *  덮는 쪽이었고(08-14 "WATCH 가 엄청 많다") 이 스위치가 접으려던 대상이다.
 *  cand_long_live=false 때 강제로 남기면 같은 알림 bh_skip 과 중복된다(08-26).
 *  편승(pr_candidate)은 active 주문 레인이라 청캐처럼 남긴다(2026-08-27). */
export function isLiveLiqWatch(
  alert: BinanceShortLiveEvent,
  nowMs: number = Date.now(),
): boolean {
  if (alert.action !== 'liq_watch' || !alert.time_kst) return false;
  const ms = kstToMs(alert.time_kst);
  if (!Number.isFinite(ms)) return false;
  return nowMs <= ms + (alert.watch_min ?? LIQ_WATCH_MIN_DEFAULT) * 60000;
}

/** 후보-롱 감시가 아직 창 안인가 (2026-08-26).
 *
 *  1초 mark 꼬리를 행 key 로 붙이면서(fb7ba1f) 같은 perp 의 bh_skip 꼬리가
 *  cl_candidate ALERT 행에 새던 게 막혔다 — 그래서 싸이식·제타체인 ALERT 에서
 *  노란점·% 가 사라졌다. 감시행 자체를 폴링 대상에 넣으려면 이 판정이 필요하다. */
export function isLiveClWatch(
  alert: BinanceShortLiveEvent,
  nowMs: number = Date.now(),
): boolean {
  if (alert.action !== 'cl_candidate' || !alert.time_kst) return false;
  const ms = kstToMs(alert.time_kst);
  if (!Number.isFinite(ms)) return false;
  return nowMs <= ms + (alert.watch_min ?? CL_WATCH_MIN_DEFAULT) * 60000;
}

/** 편승 감시가 아직 창 안인가 (2026-08-27). 진입(예정) · mark 꼬리 폴링.
 *
 *  `allowedExchanges` 가 있으면(yaml pump_ride_exchanges) 그 밖 거래소
 *  pr_candidate 는 죽은 감시로 본다 — 배포 전 업비트 행이 180분 창으로
 *  진입(예정)에 남는 구멍 (2026-08-27). */
export function isLivePrWatch(
  alert: BinanceShortLiveEvent,
  nowMs: number = Date.now(),
  allowedExchanges?: string[] | null,
): boolean {
  if (alert.action !== 'pr_candidate' || !alert.time_kst) return false;
  if (allowedExchanges && allowedExchanges.length > 0) {
    const ex = alert.exchange ?? 'upbit';
    if (!allowedExchanges.includes(ex)) return false;
  }
  const ms = kstToMs(alert.time_kst);
  if (!Number.isFinite(ms)) return false;
  return nowMs <= ms + (alert.watch_min ?? PR_WATCH_MIN_DEFAULT) * 60000;
}

/** 쌍숏 되돌림 대기 중인가 (2026-09-02). ds_candidate + watch_min(기본 30).
 *  pr_candidate 와 같은 자리 — 진입(예정) 목록 · mark 꼬리 폴링 대상. */
export function isLiveDsWatch(
  alert: BinanceShortLiveEvent,
  nowMs: number = Date.now(),
): boolean {
  if (alert.action !== 'ds_candidate' || !alert.time_kst) return false;
  const ms = kstToMs(alert.time_kst);
  if (!Number.isFinite(ms)) return false;
  return nowMs <= ms + (alert.watch_min ?? DS_WATCH_MIN_DEFAULT) * 60000;
}

/** 겹롱 15초 대기 중인가. 창이 지나 SKIP 으로 찍힌 줄은 여기 아니다. */
export function isLiveGlWatch(
  alert: BinanceShortLiveEvent,
  nowMs: number = Date.now(),
): boolean {
  if (alert.action !== 'gl_watch' || !alert.time_kst) return false;
  const ms = kstToMs(alert.time_kst);
  if (!Number.isFinite(ms)) return false;
  return nowMs <= ms + (alert.watch_sec ?? 15) * 1000;
}

/** 빗숏 되돌림 대기 중인가 (2026-08-25). bh_signal + watch_min. */
export function isLiveBhPending(
  alert: BinanceShortLiveEvent,
  delayMin: number | null | undefined = null,
  nowMs: number = Date.now(),
): boolean {
  if (alert.action !== 'bh_signal') return false;
  return pendingEntryHm(alert, delayMin, nowMs) != null;
}

export function pendingEntryHm(
  alert: BinanceShortLiveEvent,
  delayMin: number | null | undefined,
  nowMs: number = Date.now(),
): string | null {
  if (!alert.time_kst) return null;
  const ms = kstToMs(alert.time_kst);
  if (!Number.isFinite(ms)) return null;
  // 되돌림 대기(watch_min 이 실린 signal/bh_signal, 2026-08-25) 는 고정 지연이
  // 아니라 감시창이 끝날 때까지 PENDING 이다 -- 옛 고정지연(delayMin, 되돌림
  // 자동 진입이 켜지며 정체된 값)으로 재면 실제로는 아직 감시 중인데 화면에서
  // 사라진다(KRW-VIRTUAL 00:03 알림, 유저: "SKIP 이라는 글자도 안 나오고").
  // liq_watch·cl_candidate 가 watch_min 을 쓰는 것과 같은 자리(isLiveLiqWatch).
  const effDelay = alert.watch_min != null ? alert.watch_min + 1 : delayMin;
  if (effDelay == null) return null;
  const at = ms + effDelay * 60000;
  if (nowMs > at + 3 * 60000) return null;
  const d = new Date(at + 9 * 3600000);      // KST 벽시계로 찍는다
  return `${String(d.getUTCHours()).padStart(2, '0')}:`
    + `${String(d.getUTCMinutes()).padStart(2, '0')}`;
}

/** 1분봉 차트 뼈대 뒤에 mark@1s 꼬리를 붙인다 (2026-08-25 watch-live).
 *  WATCH 프리차트·OPEN 본차트 둘 다 쓴다. */
export function mergePreChartLiveTail(
  pre: [number, number][] | null | undefined,
  live: [number, number][] | null | undefined,
): [number, number][] | null {
  const base = (pre && pre.length >= 2) ? pre : null;
  const tail = (live && live.length > 0) ? live : null;
  if (!base && !tail) return null;
  if (!base) return (tail && tail.length >= 2) ? tail : null;
  if (!tail) return base;
  const lastT = base[base.length - 1][0];
  const extra = tail.filter((p) => p[0] > lastT);
  return extra.length ? [...base, ...extra] : base;
}

/** 이 행이 실제로 주문이 나간 행인가 (OPEN 이거나 이미 EXIT).
 *
 *  판정은 isPositionEntry 하나만 쓴다 -- 같은 목록을 여기서 다시 쓰면 레인이
 *  늘 때 한쪽만 고쳐진다. skip_late / skip_slot_full / entry_fail 은
 *  ENTRY_OUTCOME_ACTIONS 라 row.entry 에는 붙지만 주문은 안 나갔다(화면에도
 *  SKIP/FAIL 로 찍힌다) -- 그 둘을 섞는 게 이 필터의 유일한 함정이다. */
export function isEnteredRow(r: BinanceAlertRow): boolean {
  return isPositionEntry(r.entry?.action);
}

/** 수동 진입 버튼 대상 (2026-08-25). v1: liq·upbit — WATCH/PENDING + 보유창 안 SKIP. */
export type ManualEntryTarget = { lane: 'liq' | 'upbit' | 'bh'; sid: string };

export type ManualEntryTimingCfg = {
  entry_delay_min?: number | null;
  watch_min?: number | null;
  hold_after_min?: number | null;
  liq_cascade_hold_min?: number | null;
  /** 빗숏 알림기준 홀드(폴백). 되돌림 ON 이면 bh_hold_after_min 우선. */
  bh_hold_exit_min?: number | null;
  /** 빗숏 실보유(분). API config.bh_hold_after_min. */
  bh_hold_after_min?: number | null;
  /** 빗숏 감시(분). API config.bh_watch_min. */
  bh_watch_min?: number | null;
  /** yaml pump_ride_exchanges — isLivePrWatch 화이트리스트. */
  pump_ride_exchanges?: string[];
};

/** 화면에 SKIP 으로 찍히는 행인가 — 머리가 스킵(인테이크 · liq_skip · pr_skip)
 *  이거나, 결말이 주문 없이 접힌 스킵. FAIL 은 여기 넣지 않는다(주문은 나갔다).
 *  WATCH(liq_watch · cl_candidate · pr_candidate)와 PENDING 은 스킵이 아니다. */
export function isSkipRow(r: BinanceAlertRow): boolean {
  const skipAction = (ev: BinanceShortLiveEvent): boolean => (
    isIntakeSkip(ev)
    || ev.action.startsWith('skip_')
    || ev.action.startsWith('bh_skip_')
    || ev.action === 'liq_skip'
    || ev.action === 'pr_skip'
    || ev.action === 'ds_skip'
    || ev.action === 'gl_skip'
  );
  if (skipAction(r.alert)) return true;
  return !!r.entry && !isPositionEntry(r.entry.action) && skipAction(r.entry);
}

/**
 * "진입(예정)" 켜질 때 남길 행 — **WATCH/PENDING + 진입한 행(OPEN/EXIT)**.
 *
 * SKIP 은 전부 접는다 (2026-08-30 유저 지시). 08-25 에는 보유창 안 SKIP 을
 * 남겼는데, 그건 그 행에 수동 진입 버튼이 붙었기 때문이다 — 그 버튼이 PnL 칸에서
 * 빠지면서 남길 이유도 같이 사라졌다.
 *
 * 만료된 감시·나머지 인테이크 노이즈는 접는다. 살아 있는 청캐·편승 감시는 그
 * "곧"에 해당해서 남긴다(isLiveLiqWatch / isLivePrWatch). 후보롱 cl_candidate 는
 * 넣지 않는다 — neededOnly 가 끈 레인과 같은 알림의 bh 행이 이미 있다(2026-08-26
 * 중복). 편승은 자기 책(open_pr)이라 중복이 없다(2026-08-27 유저: active면 보여).
 */
export function filterEnteredOnlyRows(
  rows: BinanceAlertRow[],
  entryDelayMinOrCfg: number | null | undefined | ManualEntryTimingCfg,
  nowMs: number = Date.now(),
): BinanceAlertRow[] {
  const cfg: ManualEntryTimingCfg = (entryDelayMinOrCfg != null
    && typeof entryDelayMinOrCfg === 'object')
    ? entryDelayMinOrCfg
    : { entry_delay_min: entryDelayMinOrCfg as number | null | undefined };
  const entryDelayMin = cfg.entry_delay_min;
  return rows.filter((r) => {
    if (isEnteredRow(r)) return true;
    // 겹롱 주문 실패는 포지션이 아니다. 진입(예정)이 켜져 있어도 행은 남긴다.
    if (r.alert.action === 'gl_entry_fail' || r.entry?.action === 'gl_entry_fail') {
      return true;
    }
    // 마진 거절도 같은 자리다. +2% 미달 같은 SKIP 은 그대로 접는다.
    if ((r.entry?.action === 'gl_skip' && r.entry.reason === 'margin')
        || (r.alert.action === 'gl_skip' && r.alert.reason === 'margin')) {
      return true;
    }
    // 15초 안인 겹롱 대기만 예정이다. SKIP 으로 끝난 줄은 다른 레인과 같이 접는다.
    if (isLiveGlWatch(r.alert, nowMs) && !r.entry) return true;
    // SKIP 은 머리든 결말이든 접는다 (2026-08-30). 인테이크 스킵 행은 entry 가
    // 곧 자기 자신이라 아래 `r.entry` 줄에 걸리지 않는 것이 있다(skip_runup_low
    // 처럼 알림 자체가 머리인 것 · liq_skip). isSkipRow 가 그 둘을 다 본다.
    if (isSkipRow(r)) return false;
    if (r.entry) return false;
    if (isLiveLiqWatch(r.alert, nowMs)) return true;
    if (isLivePrWatch(r.alert, nowMs, cfg.pump_ride_exchanges)) return true;
    if (isLiveDsWatch(r.alert, nowMs)) return true;
    if (isLiveBhPending(r.alert, entryDelayMin, nowMs)) return true;
    // 편승·후보롱·쌍숏 감시는 위 live 판정만. 업숏 pendingEntryHm 폴백에 타면
    // 창이 끝난 뒤에도 +3분 더 남는다(pr/ds exchange 가 upbit 일 수 있다).
    if (lane(r.alert) === 'pr' || lane(r.alert) === 'cl'
        || lane(r.alert) === 'ds') return false;
    if ((r.alert.exchange ?? 'upbit') !== 'upbit') return false;
    return pendingEntryHm(r.alert, entryDelayMin, nowMs) != null;
  });
}

/** 알림 + 감시 + 보유 창이 아직 열려 있는가 (반사실 라벨과 같은 합). */
export function withinManualEntryHoldWindow(
  row: BinanceAlertRow,
  cfg: ManualEntryTimingCfg | null | undefined,
  nowMs: number = Date.now(),
): boolean {
  const alert = row.alert;
  if (!alert.time_kst) return false;
  const ms = kstToMs(alert.time_kst);
  if (!Number.isFinite(ms)) return false;
  const isLiq = lane(alert) === 'liq' || alert.action.startsWith('liq_')
    || alert.action.startsWith('liqv2_')
    || alert.action.startsWith('liqv3_');
  const isBh = lane(alert) === 'bh'
    || alert.sleeve === 'bh_newhigh'
    || alert.action.startsWith('bh_');
  const watch = alert.watch_min
    ?? (isLiq ? LIQ_WATCH_MIN_DEFAULT
      : isBh ? (cfg?.bh_watch_min ?? 60)
        : (cfg?.watch_min ?? 60));
  const hold = isLiq
    ? (cfg?.liq_cascade_hold_min ?? 120)
    : isBh
      ? (cfg?.bh_hold_after_min ?? cfg?.bh_hold_exit_min ?? 60)
      : (cfg?.hold_after_min ?? 30);
  return nowMs <= ms + (Number(watch) + Number(hold)) * 60_000;
}

function isEntryStageSkip(ev: BinanceShortLiveEvent | null | undefined): boolean {
  if (!ev) return false;
  if (isPositionEntry(ev.action)) return false;
  if (isIntakeSkip(ev)) return false;
  if (ev.action === 'liq_skip' || ev.action === 'pr_skip'
      || ev.action === 'ds_skip') return true;
  if (ev.action.startsWith('skip_')) return true;
  if (ev.action === 'entry_fail') return true;
  return ENTRY_OUTCOME_ACTIONS.has(ev.action)
    && entryOutcomeLabel(ev) === 'SKIP';
}

/** 수동 진입 버튼이 붙는 SKIP (보유창 안).
 *  진입단계 전부 + 업숏/빗숏 인테이크 runup60 (2026-08-25).
 *  빗숏을 빼먹으면 같은 시각·같은 사유인데 업비트만 버튼이 뜬다. */
function isManualEntrySkip(ev: BinanceShortLiveEvent | null | undefined): boolean {
  if (!ev) return false;
  if (ev.action === 'skip_runup_low') return true;
  if (ev.action === 'bh_skip_runup_low') return true;
  if (ev.action === 'bh_skip_no_pullback') return true;
  if (ev.action.startsWith('bh_skip_')
      && ENTRY_OUTCOME_ACTIONS.has(ev.action)) {
    return true;
  }
  return isEntryStageSkip(ev);
}

export function manualEntryTarget(
  row: BinanceAlertRow,
  entryDelayMinOrCfg: number | null | undefined | ManualEntryTimingCfg,
  nowMs: number = Date.now(),
): ManualEntryTarget | null {
  const cfg: ManualEntryTimingCfg = (entryDelayMinOrCfg != null
    && typeof entryDelayMinOrCfg === 'object')
    ? entryDelayMinOrCfg
    : { entry_delay_min: entryDelayMinOrCfg as number | null | undefined };
  const entryDelayMin = cfg.entry_delay_min;

  if (isEnteredRow(row)) return null;

  const sid = row.alert.signal_id || row.entry?.signal_id;
  if (!sid) return null;

  // 살아 있는 WATCH / PENDING
  if (!row.entry) {
    if (isLiveLiqWatch(row.alert, nowMs)) {
      return { lane: 'liq', sid };
    }
    if (row.alert.action === 'liq_skip'
        && withinManualEntryHoldWindow(row, cfg, nowMs)) {
      return { lane: 'liq', sid };
    }
    if (isLiveBhPending(row.alert, entryDelayMin, nowMs)) {
      return { lane: 'bh', sid };
    }
    if ((row.alert.exchange ?? 'upbit') !== 'upbit') return null;
    if (row.alert.sleeve === 'bh_newhigh') return null;
    if (pendingEntryHm(row.alert, entryDelayMin, nowMs) == null) return null;
    return { lane: 'upbit', sid };
  }

  // SKIP (진입단계 + 업숏/빗숏 runup60 인테이크 등) — 보유창 안
  if (!isManualEntrySkip(row.entry) && row.alert.action !== 'liq_skip'
      && row.alert.action !== 'skip_runup_low'
      && row.alert.action !== 'bh_skip_runup_low') {
    return null;
  }
  const ln = lane(row.alert);
  if (ln !== 'liq' && ln !== 'upbit' && ln !== 'bh') return null;
  if (!withinManualEntryHoldWindow(row, cfg, nowMs)) return null;
  if (ln === 'bh') return { lane: 'bh', sid };
  if (ln === 'upbit' && row.alert.sleeve === 'bh_newhigh') return null;
  return { lane: ln, sid };
}

/** Historique L 칸의 청캐 단. v1 행은 v2. v3 진입이거나 Detail 이 v3 로
 *  시작하면 v3 — 화이트리스트 미달은 액션이 liq_skip 이라 접두만 보면 v2 가 된다. */
export function liqDisplayTier(
  row: Pick<BinanceAlertRow, 'alert' | 'entry' | 'exit'>,
): 'v2' | 'v3' | null {
  const acts = [row.alert.action, row.entry?.action, row.exit?.action];
  const isLiq = acts.some((a) => !!a && a.startsWith('liq_'))
    || row.alert.exchange === 'liq'
    || row.entry?.exchange === 'liq';
  if (!isLiq) return null;
  if (acts.some((a) => a?.startsWith('liqv3_'))) return 'v3';
  const texts = [row.entry?.detail, row.alert.detail, row.exit?.detail];
  if (texts.some((d) => (d ?? '').trimStart().startsWith('v3'))) return 'v3';
  return 'v2';
}

export { entryOutcomeLabel, isIntakeSkip };
